/**
 * Server-Sent Events client with `Last-Event-ID` reconnect semantics
 * (specification §4.4.7). The server replays durable `job_event` rows on
 * reconnect, then subscribes to Redis notifications — so the client MUST send
 * the last observed event id after a drop to avoid duplicate or missed events.
 */

export interface SseEvent {
  /** Durable job-event sequence number (the SSE `id:` field), when present. */
  id: string | null;
  /** Optional event type name (the SSE `event:` field). */
  event: string | null;
  /** The `data:` payload (multiple `data:` lines joined by `\n`). */
  data: string;
}

export interface SseConnectionOptions {
  url: string;
  headers?: Record<string, string>;
  lastEventId?: string | null;
  onEvent?: (event: SseEvent) => void;
  onOpen?: () => void;
  onError?: (error: unknown) => void;
  /**
   * First reconnect delay, in ms. It doubles on every reconnect that brought no new
   * events (up to `maxRetryDelayMs`) and resets once events arrive.
   */
  retryDelayMs?: number;
  /** Ceiling for the reconnect backoff, in ms (default 30 s). */
  maxRetryDelayMs?: number;
  /** Maximum reconnect attempts; defaults to Infinity. */
  maxRetries?: number;
  signal?: AbortSignal;
  /** Injectable fetch for tests. */
  fetch?: typeof fetch;
}

export interface SseConnection {
  abort: () => void;
  lastEventId: string | null;
}

/**
 * A pure, chunk-tolerant SSE parser. Feed it decoded text; it calls `onEvent`
 * for each complete event. It tracks the last `id:` field it has seen.
 */
export interface SseParser {
  push(chunk: string): void;
  lastId(): string | null;
}

export function createSseParser(onEvent: (event: SseEvent) => void): SseParser {
  let buffer = '';
  let dataLines: string[] = [];
  let eventType: string | null = null;
  let lastId: string | null = null;

  function dispatch(): void {
    if (dataLines.length === 0) {
      eventType = null;
      return; // comments or `event:`-only blocks do not produce an event
    }
    onEvent({ id: lastId, event: eventType, data: dataLines.join('\n') });
    dataLines = [];
    eventType = null;
  }

  return {
    push(chunk: string): void {
      buffer += chunk;
      let newline: number;
      while ((newline = buffer.indexOf('\n')) !== -1) {
        let line = buffer.slice(0, newline);
        buffer = buffer.slice(newline + 1);
        if (line.endsWith('\r')) line = line.slice(0, -1);
        if (line === '') {
          dispatch();
        } else if (line.startsWith(':')) {
          // comment — ignore
        } else {
          const colon = line.indexOf(':');
          const field = colon === -1 ? line : line.slice(0, colon);
          let value = colon === -1 ? '' : line.slice(colon + 1);
          if (value.startsWith(' ')) value = value.slice(1);
          if (field === 'event') eventType = value;
          else if (field === 'data') dataLines.push(value);
          else if (field === 'id' && !value.includes('\u0000')) lastId = value;
          // `retry` is intentionally ignored: we own the reconnect backoff.
        }
      }
    },
    lastId: () => lastId,
  };
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export function connectSse(options: SseConnectionOptions): SseConnection {
  const fetcher = options.fetch ?? globalThis.fetch;
  const retryDelayMs = options.retryDelayMs ?? 1000;
  const maxRetryDelayMs = options.maxRetryDelayMs ?? 30_000;
  const maxRetries = options.maxRetries ?? Number.POSITIVE_INFINITY;
  // Reconnects in a row that delivered nothing new: a job stream that has replayed
  // its events simply ends, so reconnecting at a fixed 1 s would poll forever.
  let quietReconnects = 0;
  const backoff = () =>
    delay(Math.min(retryDelayMs * 2 ** quietReconnects++, maxRetryDelayMs), controller.signal);
  const controller = new AbortController();
  const parentSignal = options.signal;

  let aborted = false;
  let attempts = 0;
  let lastId: string | null = options.lastEventId ?? null;

  if (parentSignal) {
    if (parentSignal.aborted) aborted = true;
    else parentSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  const connection: SseConnection = {
    get lastEventId() {
      return lastId;
    },
    abort() {
      aborted = true;
      controller.abort();
    },
  };

  async function run(): Promise<void> {
    while (!aborted && attempts < maxRetries) {
      attempts += 1;
      try {
        const headers: Record<string, string> = {
          Accept: 'text/event-stream',
          ...options.headers,
        };
        if (lastId !== null) headers['Last-Event-ID'] = lastId;

        const response = await fetcher(options.url, {
          method: 'GET',
          headers,
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          options.onError?.(new Error(`SSE connection failed: HTTP ${response.status}`));
          // A client error (bad auth, unknown job) will not fix itself on retry.
          if (response.status >= 400 && response.status < 500 && response.status !== 429) break;
          await backoff();
          continue;
        }

        options.onOpen?.();
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        const parser = createSseParser((event) => {
          if (event.id !== null) lastId = event.id;
          quietReconnects = 0;
          options.onEvent?.(event);
        });

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          parser.push(decoder.decode(value, { stream: true }));
        }
        lastId = parser.lastId() ?? lastId;
        await backoff();
      } catch (error) {
        if (aborted) break;
        options.onError?.(error);
        await backoff();
      }
    }
  }

  void run();
  return connection;
}
